//https://github.com/ngx-formly/ngx-formly/blob/bd10c37ba2de0a22f272bead3ca937e2b17ae169/demo/src/app/examples/advanced/tabs-bootstrap/tab-type.component.ts
import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, model, OnDestroy, OnInit, Renderer2, signal, viewChild, viewChildren } from '@angular/core';
import { FormControlStatus, FormsModule } from '@angular/forms';
import { FieldType, FieldTypeConfig, FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslatePipe } from '@ngx-translate/core';
import { Tab, TabList, TabPanel, TabsModule } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { debounceTime, skip, Subject, take, takeUntil } from 'rxjs';
import * as dot from 'ts-dot-prop';
import { BetaFeatureDirective } from '../../../@core/directives/beta-feature.directive';
import { I18nService } from '../../../@core/services/i18n/i18n.service';
import { createSettingsSignal } from '../../../@core/services/settings.service';
import { FormValidationStatus } from '../../../@core/types/form-validation-status.type';
import { SettingType } from '../../../generated/generated-types';
import { DOCUMENT_EDIT_CONTAINER_CLASS } from '../../../shared/layout/page-fill-host.classes';
import { FormlyFillHeightListHostDirective } from './formly-fill-height-list-host.directive';
import { layoutNodeIdFromFormlyField } from '../layout-edit-node-wrapper/layout-node-id-from-formly';
import {
  applyFormlyTabFillHeightLayout,
  FORMLY_NESTED_FILL_HEIGHT_BUMP_EVENT,
  FORMLY_TAB_FILL_HEIGHT_FIELD_CLASSES,
  FormlyTabFillHeightLayoutElements,
  removeFormlyTabFillHeightLayout,
  resolveActiveFillHeightTabPanel,
  resolveRootFormlyTabs,
} from './formly-tab-fill-height.layout';
import { removeOwnedFloatingTabs } from './remove-owned-floating-tabs';

export interface StepType {
  label: string;
  fields: FormlyFieldConfig[];
}

export type TabPanelEx = TabPanel & {
  // id?: string;
  alreadyRendered?: boolean;
}

/**
 * @description This is a custom formly field type that allows you to create tabs
 * @property floatingTabs: boolean - if true, the tabs will be displayed on top of the form
 * @property reorderTab - with this tabs can be reordered
 * @property activeIndex: boolean - if true, the tab is already activated
 * @property fillHeight: boolean - when true, active tab panel fills remaining detail content height (internal scroll).
 *   Flex classes on the field group and single list child are applied by {@link FillHeightFormlyExtension}.
 * @property for single tab - enabled: boolean - by default null(false), single tabs can be enabled by true
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'formly-tabs',
  templateUrl: `./formly-tabs.component.html`,
  styleUrls: ['./formly-tabs.component.scss'],
  // Discrete class toggles — a single `[class]="…"` wipe races Formly/host merges and can
  // drop `formly-tabs--fill-height` (breaks fill SCSS + FormlyFillHeightListHostDirective).
  // Bind the cached signal (not a method): fill-height is independent of viewport size,
  // but resize still runs CD and would re-walk the DOM 8× per cycle.
  host: {
    '[class.formly-tabs--fill-height]': 'activeTabFillHeight()',
    '[class.flex]': 'activeTabFillHeight()',
    '[class.min-h-0]': 'activeTabFillHeight()',
    '[class.flex-1]': 'activeTabFillHeight()',
    '[class.flex-col]': 'activeTabFillHeight()',
    '[class.h-full]': 'activeTabFillHeight()',
    '[class.w-full]': 'activeTabFillHeight()',
    '[class.overflow-hidden]': 'activeTabFillHeight()',
  },
  imports: [CommonModule, TabsModule, TooltipModule, TabPanel, FormsModule, FormlyModule, BetaFeatureDirective, TranslatePipe]
})
export class FormlyTabsComponent extends FieldType<FieldTypeConfig> implements OnInit, AfterViewInit, OnDestroy {

  readonly fillHeightFieldClasses = FORMLY_TAB_FILL_HEIGHT_FIELD_CLASSES;

  floatingTabs: boolean;
  reorderTab: { initial: number, setPlace: number };
  tabActivationDone: boolean = false;
  afterViewInitComplete = signal<boolean>(false);

  tabViewElement = viewChild("tabView", { read: ElementRef });

  _tabView = viewChild<TabList>("tabView");

  tabs = viewChildren<Tab>("tab");

  pTabsElement = viewChild('pTabs', { read: ElementRef });

  tabPanelsElement = viewChild('tabPanelsRef', { read: ElementRef });

  fillHeightListHosts = viewChildren(FormlyFillHeightListHostDirective);

  cachedValidState: FormControlStatus = 'INVALID';

  /**
   * @description the tabs array that is passed into tablist
   */
  tabsArraySig = model<FormlyFieldConfig[]>([]);

  /**
   * @description Computed signal that caches which tabs have required fields
   * Only recalculates when tabsArraySig changes
   */
  tabsWithRequiredFields = signal<boolean[]>([]);

  get tabView(): TabList {
    return this._tabView();
  }

  activeIndex = 0;
  notDisabledIndex = 0;

  floatingTabsEnabled = createSettingsSignal(SettingType.S_FLOATING_TABS_ENABLED);

  // tabPanels = viewChildren(TabPanel);

  /**
   * Subject for managing subscriptions and preventing memory leaks
   */
  private destroy$ = new Subject<void>();

  /**
   * ElementRef for the component's host element to check if tabs belong to this instance
   */
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  private fillHeightLayoutElements: FormlyTabFillHeightLayoutElements | null = null;
  private movedFloatingTabs: HTMLElement | null = null;
  private movedFloatingTabsHeader: HTMLElement | null = null;

  /**
   * Cached fill-height host class. Recomputed on tab / visibility / nested-fill changes only —
   * not on resize/CD. See {@link refreshActiveTabFillHeight}.
   */
  readonly activeTabFillHeight = signal(false);

  private cachedIsNestedFormlyTabsHost: boolean | null = null;
  private cachedIsInsideDocumentEditShell: boolean | null = null;

  /**
   *
   */
  constructor(
    private cdRef: ChangeDetectorRef,
    private i18n: I18nService,
    private renderer: Renderer2
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.field.fieldGroup) {
      this.tabsArraySig.set(this.field.fieldGroup);
    }
  }

  ngAfterViewInit(): void {

    // first value is correct for required fields to be immediate, but would trigger a lot of times
    this.field.formControl?.valueChanges
      .pipe(
        // debounceTime(1000), // Debounce to reduce frequent updates
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(x => {
        this.tabsWithRequiredFields.set(this.tabsArraySig().map(field =>
          this.hasRequiredFieldsRecursive(field.fieldGroup)
        ));
      });

    // this is necessary for further updates
    this.field.formControl?.valueChanges
      .pipe(
        // debounceTime(1000), // Debounce to reduce frequent updates
        debounceTime(1000),
        skip(1),
        takeUntil(this.destroy$)
      )
      .subscribe(x => {
        this.tabsWithRequiredFields.set(this.tabsArraySig().map(field =>
          this.hasRequiredFieldsRecursive(field.fieldGroup)
        ));
      });

    this.field.formControl?.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(x => {
        if ((x === FormValidationStatus.VALID || x === FormValidationStatus.INVALID)) {
          if (this.afterViewInitComplete()) {
            this.updateTabDisabledState();
          }
        }
      });
    // the tabView somehow doesnt always disable - if hidden tabs are in between / also reorder cannot be done with them - since i need the passed in array
    if (this.field.fieldGroup)
      this.tabsArraySig.set(this.field.fieldGroup);

    this.setReorderedTabs();
    this.setFloatingTabs();
    this.setActivatedTab();

    this.refreshActiveTabFillHeight();
    this.cdRef.detectChanges();
    this.afterViewInitComplete.set(true);
    this.setupAncestorTabPanelObserver();
    this.setupNestedFillHeightBumpListener();
    this.syncFillHeightLayout();
    this.notifyRootFillHeightBump();
  }

  /** Root listens so Anhänge can gain fill-height when nested Report Historie becomes active. */
  private setupNestedFillHeightBumpListener(): void {
    if (this.isNestedFormlyTabsHost()) {
      return;
    }
    const host = this.hostElement.nativeElement;
    const onBump = (): void => {
      this.refreshActiveTabFillHeight();
      this.cdRef.markForCheck();
      this.scheduleFillHeightLayoutSync();
    };
    host.addEventListener(FORMLY_NESTED_FILL_HEIGHT_BUMP_EVENT, onBump);
    this.destroyRef.onDestroy(() => {
      host.removeEventListener(FORMLY_NESTED_FILL_HEIGHT_BUMP_EVENT, onBump);
    });
  }

  ngOnDestroy(): void {
    this.teardownFillHeightLayout();
    this.clearMovedFloatingTabs();

    // Clean up subscriptions to prevent memory leaks
    this.destroy$.next();
    this.destroy$.complete();

    // Clear signal dependencies
    this.tabsArraySig.set([]);
    this.afterViewInitComplete.set(false);
  }

  onActiveIndexChange(index: number | string): void {
    this.activeIndex = +index;
    this.refreshActiveTabFillHeight();
    this.cdRef.markForCheck();
    this.applyFillHeightListInterimHeights();
    // Persist on field props so form rebuilds (e.g. entity-field-config) can restore the tab.
    if (this.props) {
      this.props.activatedTab = this.activeIndex;
    }
    // Only after view init — PrimeNG may emit valueChange(0) during create and would wipe the restored tab.
    if (this.afterViewInitComplete()) {
      this.persistRootTabIndexToDetail();
    }
    this.scheduleFillHeightLayoutSync();
  }

  onLayoutEditTabHeaderClick(event: Event, field: FormlyFieldConfig, tabIndex: number): void {
    const detail = this.options?.formState?.detailComponent as
      | {
          layoutEditMode?: () => boolean;
          isLayoutTreeLocked?: () => boolean;
          selectLayoutNode?: (id: string) => void;
          rememberLayoutTabPanel?: (tabSetId: string, tabIndex: number) => void;
        }
      | undefined;
    if (!detail?.layoutEditMode?.()) {
      return;
    }
    event.stopPropagation();
    this.onActiveIndexChange(tabIndex);
    detail.rememberLayoutTabPanel?.(layoutNodeIdFromFormlyField(this.field), tabIndex);
    detail.selectLayoutNode?.(layoutNodeIdFromFormlyField(field));
  }

  /** Reapply cached / fallback px heights before paint when entering a fill-height tab. */
  private applyFillHeightListInterimHeights(): void {
    if (!this.isFillHeightTab(this.tabsArraySig()[this.activeIndex])) {
      return;
    }

    for (const hostDirective of this.fillHeightListHosts()) {
      hostDirective.applyTabActivationCap();
    }
  }

  /**
   * Root detail tabs use `floatingTabs`. Nested tabBootstraps must not overwrite that index.
   */
  private persistRootTabIndexToDetail(): void {
    if (!this.props?.floatingTabs) {
      return;
    }
    const detail = this.options?.formState?.detailComponent as
      | { rememberRootTabsActiveIndex?: (index: number) => void }
      | undefined;
    detail?.rememberRootTabsActiveIndex?.(this.activeIndex);
  }

  isFillHeightTab(field: FormlyFieldConfig | undefined): boolean {
    return !!field?.props?.fillHeight && this.evaluateHideByField(field);
  }

  /**
   * Root fill-height from own tab, or (document edit only) while a nested fillHeight tab
   * is active (Report Historie under Anhänge). Never promote on product/address — that
   * collapsed nested sales lists (KD/Bestellnummer, Kundenpreise, Mengenumrechnung).
   */
  private computeActiveTabFillHeight(): boolean {
    if (!this.isInVisibleTabPanelChain()) {
      return false;
    }
    if (this.isFillHeightTab(this.tabsArraySig()[this.activeIndex])) {
      return true;
    }
    if (!this.isNestedFormlyTabsHost() && this.isInsideDocumentEditShell()) {
      return this.activePanelHasNestedFillHeightTabs();
    }
    return false;
  }

  private refreshActiveTabFillHeight(): void {
    this.activeTabFillHeight.set(this.computeActiveTabFillHeight());
  }

  private isNestedFormlyTabsHost(): boolean {
    this.cachedIsNestedFormlyTabsHost ??=
      !!this.hostElement.nativeElement.parentElement?.closest('p-tabpanel');
    return this.cachedIsNestedFormlyTabsHost;
  }

  private isInsideDocumentEditShell(): boolean {
    this.cachedIsInsideDocumentEditShell ??=
      !!this.hostElement.nativeElement.closest(`.${DOCUMENT_EDIT_CONTAINER_CLASS}`);
    return this.cachedIsInsideDocumentEditShell;
  }

  /** Nested fillHeight tab currently visible inside this root tabs' active panel. */
  private activePanelHasNestedFillHeightTabs(): boolean {
    const panels = this.tabPanelsElement()?.nativeElement as HTMLElement | null;
    const activePanel = resolveActiveFillHeightTabPanel(panels, this.activeIndex);
    return !!activePanel?.querySelector('formly-tabs.formly-tabs--fill-height');
  }

  /** Tell root tabs to re-bind fill-height after nested tab / fill class changes (document edit). */
  private notifyRootFillHeightBump(): void {
    if (!this.isNestedFormlyTabsHost() || !this.isInsideDocumentEditShell()) {
      return;
    }
    const root = resolveRootFormlyTabs(this.hostElement.nativeElement);
    root?.dispatchEvent(new CustomEvent(FORMLY_NESTED_FILL_HEIGHT_BUMP_EVENT));
  }

  /** False when this tabs host sits inside a hidden PrimeNG tab panel (nested tabs). */
  private isInVisibleTabPanelChain(): boolean {
    let el: HTMLElement | null = this.hostElement.nativeElement.parentElement;
    while (el) {
      if (el.tagName === 'P-TABPANEL' && !el.classList.contains('p-tabpanel-active')) {
        return false;
      }
      el = el.parentElement;
    }
    return true;
  }

  private setupAncestorTabPanelObserver(): void {
    const observed = new Set<Element>();
    let el: HTMLElement | null = this.hostElement.nativeElement.parentElement;

    while (el) {
      if (el.tagName === 'P-TABPANEL' && !observed.has(el)) {
        const panel = el;
        const observer = new MutationObserver(() => {
          this.refreshActiveTabFillHeight();
          this.cdRef.markForCheck();
          this.scheduleFillHeightLayoutSync();
        });
        observer.observe(panel, { attributes: true, attributeFilter: ['class'] });
        this.destroyRef.onDestroy(() => observer.disconnect());
        observed.add(panel);
      }
      el = el.parentElement;
    }
  }

  private needsFillHeightLayout(): boolean {
    const activeField = this.tabsArraySig()[this.activeIndex];
    if (this.isFillHeightTab(activeField)) {
      return true;
    }
    return !this.isNestedFormlyTabsHost()
      && this.isInsideDocumentEditShell()
      && this.activePanelHasNestedFillHeightTabs();
  }

  private scheduleFillHeightLayoutSync(): void {
    if (!this.needsFillHeightLayout()) {
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.syncFillHeightLayout();
        // After nested host class is applied — root can detect nested fillHeight.
        this.notifyRootFillHeightBump();
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('resize'));
        });
        this.cdRef.markForCheck();
      });
    });
  }

  private syncFillHeightLayout(): void {
    this.teardownFillHeightLayout();

    if (!this.needsFillHeightLayout()) {
      return;
    }

    const layoutElements = this.getFillHeightLayoutElements();
    if (!layoutElements.activeTabPanel) {
      return;
    }

    this.fillHeightLayoutElements = layoutElements;
    applyFormlyTabFillHeightLayout(layoutElements);
  }

  private getFillHeightLayoutElements(): FormlyTabFillHeightLayoutElements {
    const tabPanelsEl = this.tabPanelsElement()?.nativeElement as HTMLElement | null;
    return {
      activeTabPanel: resolveActiveFillHeightTabPanel(tabPanelsEl, this.activeIndex),
    };
  }

  private teardownFillHeightLayout(): void {
    if (this.fillHeightLayoutElements) {
      removeFormlyTabFillHeightLayout(this.fillHeightLayoutElements);
      this.fillHeightLayoutElements = null;
    }
  }

  /**
   * Base-form hide strings are still evaluated here.
   * Overlay expressions arrive as functions and must not be passed to `new Function`.
   */
  evaluateHide(expression: unknown, field?: FormlyFieldConfig): boolean {
    if (expression === true) {
      return true;
    }
    if (typeof expression === 'function') {
      const target = field ?? this.field;
      return !!expression({
        model: target?.model ?? this.model,
        options: { formState: target?.options?.formState ?? this.formState },
      });
    }
    if (typeof expression === 'string' && expression.trim() !== '') {
      const evalFunct = new Function('model', 'formState', 'field', `return ${expression};`);
      return !!evalFunct(this.model, this.formState, field ?? this.field);
    }
    return false;
  }

  evaluateHideByField(field: FormlyFieldConfig): boolean {
    try {
      return !this.evaluateHide(field.props?.hide, field) && !this.evaluateHide(field.expressions?.hide, field);
    }
    catch (e) {
      console.error(e);
      return false;
    }
  }

  allowRender2(panel: TabPanelEx): boolean {
    if (panel.alreadyRendered == true)
      return true;

    const raw = panel.value();
    if (raw === null || raw === undefined || raw === '') {
      return false;
    }
    const panelIndex = +raw;
    if (!Number.isFinite(panelIndex) || panelIndex !== this.activeIndex) {
      return false;
    }

    panel.alreadyRendered = true;
    if (this.isFillHeightTab(this.tabsArraySig()[this.activeIndex])) {
      this.scheduleFillHeightLayoutSync();
    }
    return true;
  }

  setActivatedTab() {
    let activatedTab = this.props?.activatedTab;
    // Fallback: detail component memory (Formly may drop props.activatedTab on rebuild).
    if (activatedTab == null && this.props?.floatingTabs) {
      const detail = this.options?.formState?.detailComponent as
        | { lastRootTabsActiveIndex?: number }
        | undefined;
      activatedTab = detail?.lastRootTabsActiveIndex;
    }
    // Use nullish check so tab index 0 is restored correctly.
    if (activatedTab != null && !this.tabActivationDone) {
      this.activeIndex = +activatedTab;
      this.tabActivationDone = true;
      if (this.props) {
        this.props.activatedTab = this.activeIndex;
      }
    }
  }

  updateTabDisabledState() {

    // NOT wanted right now - maybe for future use

    // let needChangeDetection = false;
    // this.tabsArraySig().forEach((tab, index) => {
    //   const tabProps = tab.props;
    //   const isDisabled = this.props.disabled || (!this.model?.id && index !== this.notDisabledIndex && !tabProps.isEnabled);

    //   if (tabProps)
    //     if (tabProps.tabDisabled !== isDisabled) {
    //       tabProps.tabDisabled = isDisabled;
    //       needChangeDetection = true;
    //     }

    // });

    // if (needChangeDetection)
    //   this.cdRef.markForCheck();
  }

  getTabsElement() {
    const tabsElement = this.tabViewElement().nativeElement;
    return tabsElement
  }

  setFloatingTabs() {

    this.floatingTabs = this.field?.props?.floatingTabs;

    if (!this.floatingTabs || this.floatingTabsEnabled() === 'false')
      return;

    const tabsElement = this.getTabsElement();
    if (!tabsElement) {
      return;
    }

    // Must stay scoped to this detail instance. Global querySelector picks the
    // page header (e.g. document edit) and a modal preview then replaces those tabs.
    const floatingHeader = this.resolveOwnFloatingHeader();
    if (!floatingHeader) {
      return;
    }

    const existingTabs = floatingHeader.querySelector('.floatingTabs');
    if (existingTabs && existingTabs !== tabsElement) {
      existingTabs.remove();
    }

    this.renderer.addClass(tabsElement, 'floatingTabs');
    this.renderer.appendChild(floatingHeader, tabsElement);
    this.movedFloatingTabs = tabsElement;
    this.movedFloatingTabsHeader = floatingHeader;
  }

  private clearMovedFloatingTabs(): void {
    removeOwnedFloatingTabs({
      tabsElement: this.movedFloatingTabs,
      floatingHeader: this.movedFloatingTabsHeader,
    });
    this.movedFloatingTabs = null;
    this.movedFloatingTabsHeader = null;
  }

  /**
   * `.floatingHeader` of the enclosing `app-widget-base-detail` (page or modal), never the first match in `document`.
   */
  private resolveOwnFloatingHeader(): HTMLElement | null {
    const host = this.hostElement.nativeElement;
    const detailRoot = host.closest('app-widget-base-detail');
    if (detailRoot) {
      return detailRoot.querySelector('.floatingHeader');
    }
    const content = host.closest('.content');
    return content?.parentElement?.querySelector(':scope > .floatingHeader') ?? null;
  }

  setReorderedTabs() {
    this.reorderTab = this.field?.props?.reorderTab;

    if (this.reorderTab) {
      const initialTab = this.tabsArraySig()[this.reorderTab.initial];
      const setPlaceIndex = this.reorderTab.setPlace;

      if (initialTab && setPlaceIndex >= 0 && setPlaceIndex < this.tabsArraySig().length) {

        let tabs = this.tabsArraySig();

        tabs.splice(this.reorderTab.initial, 1);
        tabs.splice(setPlaceIndex, 0, initialTab);

        this.tabsArraySig.set(tabs);

        // Trigger change detection to ensure the UI updates
        this.cdRef.markForCheck();
      } else {
        console.warn('Invalid tab indices for reordering.');
      }
    }
  }

  getTabTitle(field: FormlyFieldConfig): string {
    if (field?.props?.translate)
      return this.i18n.translate(field.props?.label);
    else
      return field.props?.label;
  }

  getTabDataTestId(index: number): string {
    let tab = this.field?.fieldGroup[index];
    let attributes: string[];
    if (tab)
      attributes = dot.get(tab?.props, 'attributes');
    let dataTestId: string;
    if (attributes)
      dataTestId = dot.get(attributes, 'data-testid');
    if (!attributes || dataTestId === undefined) {
      return "tab-" + index;
    }
    else {
      return dataTestId;
    }
  }

  private tabClassesCacheKey: string | null = null;
  private tabClassesSelected: { [key: string]: boolean } = { 'font-bold': true };
  private tabClassesUnselected: { [key: string]: boolean } = { 'font-bold': false };

  getClasses(tab: Tab, fieldGroup: FormlyFieldConfig): { [key: string]: boolean } {
    this.ensureTabClassesCache();
    const isSelected = +(tab.value()) == this.activeIndex;
    return isSelected ? this.tabClassesSelected : this.tabClassesUnselected;
  }

  private ensureTabClassesCache(): void {
    const activeClasses: string | undefined = this.field.props?.activeClasses;
    const cacheKey = activeClasses ?? '__default__';
    if (this.tabClassesCacheKey === cacheKey) {
      return;
    }
    this.tabClassesCacheKey = cacheKey;
    if (!activeClasses) {
      this.tabClassesSelected = { 'font-bold': true };
      this.tabClassesUnselected = { 'font-bold': false };
      return;
    }
    const selected: { [key: string]: boolean } = {};
    const unselected: { [key: string]: boolean } = {};
    for (const classString of activeClasses.split(',')) {
      const name = classString.trim();
      selected[name] = true;
      unselected[name] = false;
    }
    this.tabClassesSelected = selected;
    this.tabClassesUnselected = unselected;
  }

  /**
   * Recursively checks if any field in the given field group or its nested field groups has a required property
   * Optimized version with early returns for better performance
   * @param fields Array of FormlyFieldConfig to check
   * @returns true if any field at any nesting level is required, false otherwise
   */
  hasRequiredFieldsRecursive(fields: FormlyFieldConfig[]): boolean {
    if (!fields || !Array.isArray(fields)) {
      return false;
    }

    for (const field of fields) {
      // Early return if we find a required invalid field that's not hidden - sometimes field is valid even though it has no value - which dont make sense
      if (field.props?.required && (field.formControl?.status === 'INVALID' || !field.formControl?.value) && !field.hide) {
        return true;
      }

      // Check nested fieldGroup recursively
      if (field.fieldGroup && Array.isArray(field.fieldGroup)) {
        if (this.hasRequiredFieldsRecursive(field.fieldGroup)) {
          return true;
        }
      }
    }

    return false;
  }

  isEmpty(value: any): boolean {
    if (typeof value === 'string') {
      return value.trim() === '';
    } else if (typeof value === 'number') {
      return value === null || isNaN(value);
    } else if (typeof value === 'object') {
      return value === null || Object.keys(value).length === 0;
    } else {
      return value === null || value === undefined;
    }
  }

  getTabDataTestId2(index: number, tabTitle: string): string {
    if (!tabTitle)
      return "";

    return `tab-type-tab-${tabTitle.replace(/\s+/g, '-').toLowerCase()}-${index}`;
  }

}